var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import UploadIcon from "@mui/icons-material/Upload";
import { Alert, Avatar, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import { socket } from "api";
import { CategoryScale, Chart as ChartJS, Filler, LinearScale, LineElement, PointElement, } from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import { FONT, T } from "../../../admin.theme";
import { PageHeader, Panel } from "../../../ui";
import { useAdminCoins, useCoinUpsertImg } from "../../model/hooks/useAdminCoins";
var useLivePrices = function () {
    var _a = useState({}), live = _a[0], setLive = _a[1];
    useEffect(function () {
        var handler = function (payload) {
            setLive(function (prev) {
                var _a;
                var next = __assign({}, prev);
                for (var _i = 0, payload_1 = payload; _i < payload_1.length; _i++) {
                    var _b = payload_1[_i], symbol = _b.symbol, price = _b.price;
                    var sym = symbol.toUpperCase();
                    var prevPrice = (_a = prev[sym]) === null || _a === void 0 ? void 0 : _a.price;
                    next[sym] = {
                        price: price,
                        dir: prevPrice == null ? null : price > prevPrice ? "up" : price < prevPrice ? "down" : null,
                    };
                }
                return next;
            });
        };
        socket.on("prices:update", handler);
        return function () {
            socket.off("prices:update", handler);
        };
    }, []);
    return live;
};
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);
var NETWORK_COLORS = {
    BSC: "#f0b90b",
    ETH: "#627eea",
    POLYGON: "#8247e5",
    SOL: "#9945ff",
    BTC: "#f7931a",
};
var UP = T.success;
var DOWN = T.danger;
var WARN = "#FBBF24";
var COLS = "44px 200px 110px 72px 72px 72px 120px 120px 100px 20px 110px";
var fmt = function (n) {
    return n === 0 ? "0" : n < 0.0001 ? n.toExponential(2) : n < 0.01 ? n.toFixed(6) : n < 1 ? n.toFixed(4) : n.toFixed(2);
};
var delta = function (from, to) { return (from === 0 ? null : ((to - from) / from) * 100); };
var stdDev = function (arr) {
    if (arr.length < 2)
        return 0;
    var mean = arr.reduce(function (a, b) { return a + b; }, 0) / arr.length;
    return Math.sqrt(arr.reduce(function (a, b) { return a + Math.pow((b - mean), 2); }, 0) / arr.length);
};
var calcStats = function (history) {
    var _a;
    var prices = history.map(function (p) { return p.price; });
    if (prices.length === 0)
        return { current: null, d1: null, d7: null, d30: null, high: null, low: null, avg: null, volatility: null, range: null };
    var current = (_a = prices.at(-1)) !== null && _a !== void 0 ? _a : null;
    var prev1 = prices.length >= 2 ? prices.at(-2) : null;
    var prev7 = prices.length >= 7 ? prices.at(-7) : null;
    var prev30 = prices.length >= 30 ? prices[0] : null;
    var high = Math.max.apply(Math, prices);
    var low = Math.min.apply(Math, prices);
    var avg = prices.reduce(function (a, b) { return a + b; }, 0) / prices.length;
    var vol = current && avg > 0 ? (stdDev(prices) / avg) * 100 : null;
    var range = high > 0 ? ((high - low) / high) * 100 : null;
    return {
        current: current,
        d1: current !== null && prev1 !== null ? delta(prev1, current) : null,
        d7: current !== null && prev7 !== null ? delta(prev7, current) : null,
        d30: current !== null && prev30 !== null ? delta(prev30, current) : null,
        high: high,
        low: low,
        avg: avg,
        volatility: vol,
        range: range,
    };
};
var TableCardBox = styled(Panel)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  overflow: hidden;\n  width: 100%;\n"], ["\n  overflow: hidden;\n  width: 100%;\n"])));
var HeaderRow = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: grid;\n  grid-template-columns: ", ";\n  gap: 16px;\n  padding: 14px 20px;\n  background: ", ";\n  border-bottom: 1px solid ", ";\n"], ["\n  display: grid;\n  grid-template-columns: ", ";\n  gap: 16px;\n  padding: 14px 20px;\n  background: ", ";\n  border-bottom: 1px solid ", ";\n"])), COLS, T.surface2, T.border);
var RowBox = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: grid;\n  grid-template-columns: ", ";\n  align-items: center;\n  gap: 16px;\n  padding: 14px 20px;\n  background: ", ";\n  border-bottom: 1px solid ", ";\n  transition: background 140ms;\n\n  &:hover {\n    background: ", ";\n  }\n\n  &:last-of-type {\n    border-bottom: none;\n  }\n"], ["\n  display: grid;\n  grid-template-columns: ", ";\n  align-items: center;\n  gap: 16px;\n  padding: 14px 20px;\n  background: ", ";\n  border-bottom: 1px solid ", ";\n  transition: background 140ms;\n\n  &:hover {\n    background: ", ";\n  }\n\n  &:last-of-type {\n    border-bottom: none;\n  }\n"])), COLS, T.surface, T.border, T.surface2);
var UploadBtn = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 7px 14px;\n  border-radius: 8px;\n  border: 1px solid ", ";\n  color: ", ";\n  cursor: ", ";\n  opacity: ", ";\n  font-family: ", ";\n  font-size: 12px;\n  font-weight: 500;\n  transition: all 160ms;\n\n  &:hover {\n    ", "\n  }\n"], ["\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 7px 14px;\n  border-radius: 8px;\n  border: 1px solid ", ";\n  color: ", ";\n  cursor: ", ";\n  opacity: ", ";\n  font-family: ", ";\n  font-size: 12px;\n  font-weight: 500;\n  transition: all 160ms;\n\n  &:hover {\n    ", "\n  }\n"])), T.border, T.textSecondary, function (_a) {
    var $busy = _a.$busy;
    return ($busy ? "default" : "pointer");
}, function (_a) {
    var $busy = _a.$busy;
    return ($busy ? 0.5 : 1);
}, FONT.body, function (_a) {
    var $busy = _a.$busy;
    return $busy ? "" : "border-color: ".concat(T.accentTintStrong, "; color: ").concat(T.accent, "; background: ").concat(T.accentTint, ";");
});
var LiveDot = styled.span(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: inline-block;\n  width: 8px;\n  height: 8px;\n  border-radius: 50%;\n  background: ", ";\n  animation: ", ";\n\n  @keyframes adminPulse {\n    0%,\n    100% {\n      opacity: 1;\n    }\n    50% {\n      opacity: 0.3;\n    }\n  }\n"], ["\n  display: inline-block;\n  width: 8px;\n  height: 8px;\n  border-radius: 50%;\n  background: ", ";\n  animation: ", ";\n\n  @keyframes adminPulse {\n    0%,\n    100% {\n      opacity: 1;\n    }\n    50% {\n      opacity: 0.3;\n    }\n  }\n"])), function (_a) {
    var $on = _a.$on;
    return ($on ? T.success : T.textMuted);
}, function (_a) {
    var $on = _a.$on;
    return ($on ? "adminPulse 1.5s infinite" : "none");
});
var DeltaBadge = function (_a) {
    var value = _a.value, label = _a.label;
    if (value === null)
        return _jsx(Typography, { sx: { color: T.textMuted, fontSize: 12 }, children: "\u2014" });
    var pos = value >= 0;
    return (_jsx(Tooltip, { title: label, children: _jsxs(Typography, { sx: { fontSize: 12, fontWeight: 600, color: pos ? UP : DOWN }, children: [pos ? "▲" : "▼", " ", Math.abs(value).toFixed(2), "%"] }) }));
};
var ColHeader = function (_a) {
    var children = _a.children, hint = _a.hint;
    return (_jsx(Tooltip, { title: hint !== null && hint !== void 0 ? hint : "", placement: "top", children: _jsx(Typography, { sx: {
                fontSize: 11,
                fontWeight: 600,
                color: T.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                cursor: hint ? "help" : "default",
            }, children: children }) }));
};
var CoinRow = function (_a) {
    var _b, _c, _d;
    var coin = _a.coin, liveData = _a.liveData;
    var fileRef = useRef(null);
    var _e = useCoinUpsertImg(), mutate = _e.mutate, isPending = _e.isPending, isSuccess = _e.isSuccess, isError = _e.isError;
    var _f = useState(null), previewUrl = _f[0], setPreviewUrl = _f[1];
    var handleFileChange = function (e) {
        var _a;
        var file = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (!file)
            return;
        var local = URL.createObjectURL(file);
        setPreviewUrl(local);
        mutate({ coinId: coin.id, file: file }, {
            onError: function () {
                URL.revokeObjectURL(local);
                setPreviewUrl(null);
            },
        });
        e.target.value = "";
    };
    var logoSrc = (_b = previewUrl !== null && previewUrl !== void 0 ? previewUrl : coin.logoImg) !== null && _b !== void 0 ? _b : undefined;
    var networkColor = (_c = NETWORK_COLORS[coin.network.toUpperCase()]) !== null && _c !== void 0 ? _c : T.accent;
    var stats = calcStats(coin.priceHistory);
    var prices = coin.priceHistory.map(function (p) { return p.price; });
    var trending = ((_d = stats.d30) !== null && _d !== void 0 ? _d : 0) >= 0;
    return (_jsxs(RowBox, { children: [_jsx(Avatar, { src: logoSrc, variant: "rounded", sx: {
                    width: 40,
                    height: 40,
                    borderRadius: "".concat(T.radiusSm, "px"),
                    background: T.accentTint,
                    border: "1px solid ".concat(T.accentTintStrong),
                    "& img": { objectFit: "contain" },
                }, children: _jsx(MonetizationOnIcon, { sx: { color: T.accent, fontSize: 20 } }) }), _jsxs("div", { style: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                            color: T.textPrimary,
                            fontWeight: 700,
                            fontSize: 13,
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }, children: coin.name }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6, marginTop: 3 }, children: [_jsx(Typography, { sx: { color: T.accent, fontSize: 11, fontFamily: "monospace", fontWeight: 600 }, children: coin.symbol }), _jsx(Chip, { label: coin.network, size: "small", sx: {
                                    height: 16,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: "".concat(networkColor, "1a"),
                                    color: networkColor,
                                    border: "1px solid ".concat(networkColor, "40"),
                                    "& .MuiChip-label": { px: 0.6 },
                                } })] })] }), _jsxs("div", { children: [_jsx(Typography, { sx: {
                            fontWeight: 700,
                            fontSize: 14,
                            transition: "color 300ms",
                            color: (liveData === null || liveData === void 0 ? void 0 : liveData.dir) === "up" ? UP : (liveData === null || liveData === void 0 ? void 0 : liveData.dir) === "down" ? DOWN : T.textPrimary,
                        }, children: liveData ? "$".concat(fmt(liveData.price)) : stats.current !== null ? "$".concat(fmt(stats.current)) : "—" }), liveData && _jsx(Typography, { sx: { fontSize: 10, color: T.textMuted, lineHeight: 1 }, children: "live" })] }), _jsx(DeltaBadge, { value: stats.d1, label: "\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0435 \u0437\u0430 24\u0447" }), _jsx(DeltaBadge, { value: stats.d7, label: "\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0435 \u0437\u0430 7 \u0434\u043D\u0435\u0439" }), _jsx(DeltaBadge, { value: stats.d30, label: "\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0435 \u0437\u0430 30 \u0434\u043D\u0435\u0439" }), _jsxs("div", { children: [_jsxs(Typography, { sx: { fontSize: 11, color: UP, fontWeight: 600 }, children: ["\u25B2 ", stats.high !== null ? "$".concat(fmt(stats.high)) : "—"] }), _jsxs(Typography, { sx: { fontSize: 11, color: DOWN, fontWeight: 600 }, children: ["\u25BC ", stats.low !== null ? "$".concat(fmt(stats.low)) : "—"] })] }), _jsxs("div", { children: [_jsx(Tooltip, { title: "\u0421\u0440\u0435\u0434\u043D\u044F\u044F \u0446\u0435\u043D\u0430 (30\u0434)", children: _jsxs(Typography, { sx: { fontSize: 11, color: T.textSecondary }, children: ["\u00D8 ", stats.avg !== null ? "$".concat(fmt(stats.avg)) : "—"] }) }), _jsx(Tooltip, { title: "\u0412\u043E\u043B\u0430\u0442\u0438\u043B\u044C\u043D\u043E\u0441\u0442\u044C = StdDev / Avg \u00D7 100", children: _jsxs(Typography, { sx: { fontSize: 11, color: stats.volatility !== null && stats.volatility > 20 ? WARN : T.textMuted }, children: ["\u03C3 ", stats.volatility !== null ? "".concat(stats.volatility.toFixed(1), "%") : "—"] }) })] }), _jsx("div", { style: { height: 34 }, children: prices.length > 1 ? (_jsx(Line, { data: {
                        labels: prices.map(function (_, i) { return i; }),
                        datasets: [
                            {
                                data: prices,
                                borderColor: trending ? UP : DOWN,
                                backgroundColor: trending ? "rgba(40,199,111,0.08)" : "rgba(220,53,69,0.08)",
                                borderWidth: 1.5,
                                pointRadius: 0,
                                tension: 0.4,
                                fill: true,
                            },
                        ],
                    }, options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        animation: false,
                        plugins: { legend: { display: false }, tooltip: { enabled: false } },
                        scales: { x: { display: false }, y: { display: false } },
                    } })) : (_jsx(Typography, { sx: { color: T.textMuted, fontSize: 11, lineHeight: "34px" }, children: "\u2014" })) }), _jsxs("div", { style: { display: "flex", justifyContent: "center" }, children: [isSuccess && _jsx(CheckCircleIcon, { sx: { color: UP, fontSize: 17 } }), isError && _jsx(ErrorOutlineIcon, { sx: { color: DOWN, fontSize: 17 } })] }), _jsx("input", { ref: fileRef, type: "file", accept: "image/png,image/jpeg,image/webp,image/svg+xml", style: { display: "none" }, onChange: handleFileChange }), _jsxs(UploadBtn, { "$busy": isPending, onClick: function () { var _a; return !isPending && ((_a = fileRef.current) === null || _a === void 0 ? void 0 : _a.click()); }, children: [isPending ? _jsx(CircularProgress, { size: 12, sx: { color: T.accent } }) : _jsx(UploadIcon, { sx: { fontSize: 14 } }), isPending ? "…" : "Загрузить"] })] }));
};
var HEADERS = [
    { label: "", hint: undefined },
    { label: "Монета", hint: undefined },
    { label: "Цена", hint: undefined },
    { label: "24ч", hint: "Изменение за 24 часа" },
    { label: "7д", hint: "Изменение за 7 дней" },
    { label: "30д", hint: "Изменение за 30 дней" },
    { label: "High / Low", hint: "Макс. и мин. цена за 30 дней" },
    { label: "Avg / Vol", hint: "Средняя цена и волатильность (σ/avg)" },
    { label: "График 30д", hint: undefined },
    { label: "", hint: undefined },
    { label: "Логотип", hint: undefined },
];
export var CoinImageManager = function () {
    var _a = useAdminCoins(), coins = _a.data, isLoading = _a.isLoading, isError = _a.isError;
    var live = useLivePrices();
    var isLive = Object.keys(live).length > 0;
    if (isLoading)
        return (_jsx(Panel, { style: { display: "flex", justifyContent: "center", padding: 64 }, children: _jsx(CircularProgress, { sx: { color: T.accent } }) }));
    if (isError)
        return _jsx(Alert, { severity: "error", variant: "outlined", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043C\u043E\u043D\u0435\u0442\u044B." });
    var coinList = coins !== null && coins !== void 0 ? coins : [];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "Coin Images", subtitle: _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: 8 }, children: ["".concat(coinList.length, " \u043C\u043E\u043D\u0435\u0442 \u00B7 \u0446\u0435\u043D\u044B \u0437\u0430 30 \u0434\u043D\u0435\u0439"), _jsx(LiveDot, { "$on": isLive }), _jsx("span", { style: { color: isLive ? T.success : T.textMuted, fontSize: 11, fontWeight: 600 }, children: isLive ? "LIVE" : "WAITING" })] }) }), coinList.length === 0 ? (_jsxs(Panel, { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 64 }, children: [_jsx(MonetizationOnIcon, { style: { color: "rgba(255,255,255,0.12)", fontSize: 46 } }), _jsx("span", { style: { color: T.textMuted, fontSize: 14 }, children: "\u041C\u043E\u043D\u0435\u0442 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" })] })) : (_jsxs(TableCardBox, { children: [_jsx(HeaderRow, { children: HEADERS.map(function (h, i) { return (_jsx(ColHeader, { hint: h.hint, children: h.label }, i)); }) }), coinList.map(function (coin) { return (_jsx(CoinRow, { coin: coin }, coin.id)); })] }))] }));
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;
